import { Component, OnDestroy, OnInit } from "@angular/core";
import { Tuple } from "@tylertech/tyler-api-types";
import { JournalTemplateService } from "../../services";
import { SelectionChange, SelectionModel } from "@angular/cdk/collections";
import { FormGroup, AbstractControl, FormArray, ValidationErrors } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { Observable, Subject } from "rxjs";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { takeUntil } from "rxjs/operators";

interface JournalEntryTemplateLike
{
    Id: string;
    TemplateName: string;
    TemplateDescription: string;
    ControllingFund: string;
    JournalTemplateTypeDescription: string;
    JournalEntryTypeDescription: string;
    JournalTemplateTypeValue: "P" | undefined;
}

type JournalEntryTemplateColumn = "selected" | keyof JournalEntryTemplateLike;
type FilterState<T extends string = string> = Partial<Record<T, string>>;

@Component({
    selector: "journal-entry-template-dialog-component",
    templateUrl: "./journalEntryTemplateDialog.tmpl.html",
    standalone: false
})
export class JournalEntryTemplateDialogComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly tableRowsDestructor = new Subject<void>();
    private readonly filterState: FilterState<JournalEntryTemplateColumn> = {};

    readonly tableFilterFormGroup: FormGroup;
    readonly tableFormGroup: FormGroup;
    readonly formGroup: FormGroup;
    readonly columnNames: ReadonlyArray<JournalEntryTemplateColumn> = [
        "selected",
        "TemplateName",
        "TemplateDescription",
        "ControllingFund",
        "JournalTemplateTypeDescription",
        "JournalEntryTypeDescription",
    ];

    private selectionManager: SelectionModel<JournalEntryTemplateLike>;

    tableData: MatTableDataSource<JournalEntryTemplateLike>;
    isLoaded: boolean;

    constructor(
        private $glJournalTemplateService: JournalTemplateService)
    {
        const initialSelection = [];
        const allowMultiSelect = false;
        this.selectionManager = new SelectionModel<JournalEntryTemplateLike>(allowMultiSelect, initialSelection);
        this.tableData = new MatTableDataSource<JournalEntryTemplateLike>();
        this.tableData.filterPredicate = (data: JournalEntryTemplateLike, filter: string): boolean =>
        {
            const state = JSON.parse(filter) as FilterState<JournalEntryTemplateColumn>;
            return Object.entries(state)
                .reduce(
                    (result: boolean, [column, filterValue]: [string, string]): boolean =>
                    {
                        if (!result || !filterValue)
                            return result;

                        const value: string | undefined = data[column]?.toString().toLowerCase();
                        if (!value || value.indexOf(filterValue) === -1)
                        {
                            return false;
                        }
                        return true;
                    },
                    true);
        };

        this.tableFilterFormGroup = new FormGroup({
            templateNameFilter: LgdFormControlFactory.text(),
            templateDescriptionFilter: LgdFormControlFactory.text(),
            controllingFundFilter: LgdFormControlFactory.text(),
            templateTypeDescriptionFilter: LgdFormControlFactory.text(),
            typeDescriptionFilter: LgdFormControlFactory.text(),
        });

        this.tableFormGroup = new FormGroup({
            rows: new FormArray([
                this.tableFilterFormGroup
            ])
        });

        this.formGroup = new FormGroup(
            {
                distributionAmount: LgdFormControlFactory.currency(
                    undefined,
                    () => this.selectedTemplate?.JournalTemplateTypeValue === "P"
                ),
            },
            [
                (_control: AbstractControl): ValidationErrors | null =>
                {
                    if (!this.hasSelection)
                    {
                        return { "selection": true };
                    }
                    return null;
                },
            ]);
    }

    get returnObject(): Tuple<string, number>
    {
        return {
            Item1: this.selectedTemplate.Id,
            Item2: this.distributionAmount,
        };
    }

    private get templateNameFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("templateNameFilter"); }
    private get templateDescriptionFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("templateDescriptionFilter"); }
    private get controllingFundFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("controllingFundFilter"); }
    private get templateTypeDescriptionFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("templateTypeDescriptionFilter"); }
    private get typeDescriptionFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("typeDescriptionFilter"); }

    private get distributionAmountControl(): AbstractControl<number> { return this.formGroup.get("distributionAmount"); }
    private get distributionAmount(): number { return this.distributionAmountControl.value; }
    private get rowsArray(): FormArray { return this.tableFormGroup.get("rows") as FormArray; }

    private get hasSelection(): boolean
    {
        return this.selectionManager.selected?.length === 1;
    }

    get selectedTemplate(): JournalEntryTemplateLike
    {
        return this.selectionManager.selected?.length === 1
            ? this.selectionManager.selected[0]
            : undefined;
    }

    ngOnDestroy(): void
    {
        this.tableRowsDestructor.next();
        this.tableRowsDestructor.complete();

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.$glJournalTemplateService.GetGLJournalTemplates()
            .then((templates: Array<JournalEntryTemplateLike>) =>
            {
                this.initializeTableRows(templates);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        this.subscribeTo(this.selectionManager.changed, (_selectionChange: SelectionChange<JournalEntryTemplateLike>) =>
        {
            setEnabled(
                this.distributionAmountControl,
                this.hasSelection && this.selectedTemplate?.JournalTemplateTypeValue === "P");
        });

        this.subscribeToValueChanges(this.templateNameFilterControl, (newValue: string) =>
        {
            this.filterState.TemplateName = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.templateDescriptionFilterControl, (newValue: string) =>
        {
            this.filterState.TemplateDescription = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.controllingFundFilterControl, (newValue: string) =>
        {
            this.filterState.ControllingFund = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.templateTypeDescriptionFilterControl, (newValue: string) =>
        {
            this.filterState.JournalTemplateTypeDescription = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.typeDescriptionFilterControl, (newValue: string) =>
        {
            this.filterState.JournalEntryTypeDescription = newValue.toLowerCase();

            this.updateTableFilter();
        });
    }

    private subscribeTo<T = unknown>(observable: Observable<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        observable.pipe(takeUntil(destructor || this.destructor$))
            .subscribe(x =>
            {
                subscriptionFn(x);
            });
    }

    private subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        this.subscribeTo(control.valueChanges, subscriptionFn, destructor);
    }

    private updateTableFilter(): void
    {
        this.tableData.filter = JSON.stringify(this.filterState);
    }

    private initializeTableRows(values: Array<JournalEntryTemplateLike>): void
    {
        this.tableRowsDestructor.next();
        this.rowsArray.clear();
        this.rowsArray.push(this.tableFilterFormGroup);

        this.tableData.data = values;
        this.tableData.data
            .forEach(
                r =>
                {
                    const selectRowControl = LgdFormControlFactory.checkbox();
                    const rowFormGroup = new FormGroup({ selected: selectRowControl });
                    this.rowsArray.push(rowFormGroup);

                    this.subscribeToValueChanges(selectRowControl, (_newValue: boolean) =>
                    {
                        this.selectionManager.toggle(r);
                    });
                });
    }
}
